"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface CTAFormState {
  name: string;
  os: string;
  channels: string[];
  message: string;
}

const defaultForm: CTAFormState = { name: "", os: "", channels: [], message: "" };
const CHANNEL_OPTIONS = ["WhatsApp", "Telegram", "Discord", "Slack", "Signal", "iMessage"];

export const CTASection = () => {
  const [form, setForm] = useState<CTAFormState>(defaultForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleChannel = (ch: string) => {
    setForm((prev) => ({
      ...prev,
      channels: prev.channels.includes(ch)
        ? prev.channels.filter((c) => c !== ch)
        : [...prev.channels, ch],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please fill in your name and message.");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: response, error } = await supabase.functions.invoke("submit-form", {
        body: {
          email: `${form.name.trim().replace(/\s+/g, ".")}@cta-form.clawbiz`,
          type: "quote",
          data: {
            email: `${form.name.trim().replace(/\s+/g, ".")}@cta-form.clawbiz`,
            estimated_price: 0,
            status: "new",
            quote_details: {
              source: "clawbiz-cta-form",
              name: form.name.trim(),
              os: form.os,
              channels: form.channels,
              message: form.message.trim(),
              capturedAt: new Date().toISOString(),
            },
          },
        },
      });

      if (error || !response?.success) {
        toast.error(response?.error || "Could not send your request. Please try again.");
        return;
      }

      toast.success("Request sent! We'll get back to you soon.");
      setForm(defaultForm);
    } catch {
      toast.error("Unexpected error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-spacing relative overflow-hidden">
      <div className="section-container relative z-10">
        <motion.div
          className="max-w-3xl mx-auto text-center space-y-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-3xl md:text-5xl font-display font-medium leading-tight">
            Tell us what you want your AI to <span className="gradient-text">actually do</span>.
          </h2>
          <p className="text-lg text-muted-foreground">
            We'll come back with a concrete setup plan and whether we're the right fit.
          </p>

          <form onSubmit={handleSubmit} className="max-w-lg mx-auto glass-card rounded-2xl p-6 md:p-8 space-y-5 text-left relative">
            <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-br from-[hsl(var(--coral-bright)/0.15)] via-transparent to-[hsl(var(--cyan-bright)/0.15)] pointer-events-none" />

            <div className="relative space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground/80">Name / handle</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="@your-handle"
                  required
                  className="w-full rounded-xl bg-white/[0.05] px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(var(--coral-bright)/0.4)] transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground/80">OS / infra</label>
                <select
                  value={form.os}
                  onChange={(e) => setForm((p) => ({ ...p, os: e.target.value }))}
                  className="w-full rounded-xl bg-white/[0.05] px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(var(--coral-bright)/0.4)] transition-all"
                >
                  <option value="">Select…</option>
                  <option>macOS</option>
                  <option>Windows</option>
                  <option>Linux</option>
                  <option>VPS / Cloud</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground/80">Channels you care about</label>
                <div className="flex flex-wrap gap-2">
                  {CHANNEL_OPTIONS.map((ch) => (
                    <label
                      key={ch}
                      className={`flex items-center gap-1.5 text-sm cursor-pointer px-3 py-1.5 rounded-lg transition-colors ${
                        form.channels.includes(ch)
                          ? "bg-primary/20 text-primary"
                          : "text-muted-foreground bg-white/[0.04] hover:bg-white/[0.08]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={form.channels.includes(ch)}
                        onChange={() => toggleChannel(ch)}
                        className="rounded accent-primary"
                      />
                      {ch}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground/80">What do you wish Clawbiz was already doing for you?</label>
                <textarea
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                  placeholder="Tell us…"
                  required
                  className="w-full rounded-xl bg-white/[0.05] px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(var(--coral-bright)/0.4)] resize-none transition-all"
                />
              </div>
              <Button type="submit" size="lg" className="w-full glow-primary" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {isSubmitting ? "Sending…" : "Send setup request"}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
